import type { ApiCondo } from './api/ApiCondo';
import type { ApiProperty } from './api/ApiProperty';
import type {
	CondoFeatureCategory,
	CondoFeatureEnum,
	CondoFeatureObject,
	CondoStatusEnum
} from './CRMCondoEnums';
import type { FeatureCategory, FeatureEnum, FeatureObject } from './CRMEnums';
import type { Property } from './property';

import { CondoTypeEnum } from './CRMCondoEnums';
import { PropertyTypeEnum } from './CRMEnums';

export type SystemEnums = {
	categories: Record<FeatureCategory, string>;
	developmentStage: Record<string, string>;
	displayAddress: Record<string, string>;
	features: Record<FeatureEnum, FeatureObject>;
	frontDeskHours: Record<string, string>;
	propertyTypes: Record<PropertyTypeEnum, string>;
	role: Record<string, string>;
	transactionTypes: Record<string, string>;
};

export type CondoSystemEnums = {
	features: Record<CondoFeatureEnum, CondoFeatureObject>;
	types: Record<CondoTypeEnum, string>;
	status: Record<CondoStatusEnum, string>;
	categories: Record<CondoFeatureCategory, string>;
};

export function getAreaLabel(
	propertyType: PropertyTypeEnum,
	displayOnCard = false
) {
	const areaOptions = {
		land: 'Área do terreno',
		condo_land: 'Área do terreno',
		house: 'Área construída',
		condo_house: 'Área construída'
	} as Record<PropertyTypeEnum, string>;

	const cardAreaOptions = {
		land: 'terreno',
		condo_land: 'terreno',
		house: 'construídos',
		condo_house: 'construídos'
	} as Record<PropertyTypeEnum, string>;

	const options = displayOnCard ? cardAreaOptions : areaOptions;

	const fallback = displayOnCard ? 'Útil' : 'Área útil';

	return options[propertyType] || fallback;
}

export const hiddenFields = readonly<
	Record<PropertyTypeEnum, (keyof Property)[]>
>({
	[PropertyTypeEnum.LAND]: [
		'bathrooms',
		'bedrooms',
		'parkingSpots',
		'suites',
		'totalArea',
		'launchDate'
	],
	[PropertyTypeEnum.COMMERCIAL]: ['bedrooms', 'suites'],
	[PropertyTypeEnum.COMMERCIAL_BUILDING]: ['bedrooms', 'suites'],
	[PropertyTypeEnum.COMMERCIAL_POINT]: ['bedrooms', 'suites'],
	[PropertyTypeEnum.COMMERCIAL_SET]: ['bedrooms', 'suites'],
	[PropertyTypeEnum.CONDO_HOUSE]: [],
	[PropertyTypeEnum.CONDO_LAND]: [
		'bathrooms',
		'bedrooms',
		'suites',
		'totalArea',
		'parkingSpots',
		'launchDate'
	],
	[PropertyTypeEnum.HOUSE]: [],
	[PropertyTypeEnum.VILLAGE_HOUSE]: [],
	[PropertyTypeEnum.APARTMENT]: [],
	[PropertyTypeEnum.DUPLEX]: [],
	[PropertyTypeEnum.FLAT]: [],
	[PropertyTypeEnum.GARDEN]: [],
	[PropertyTypeEnum.LOFT]: [],
	[PropertyTypeEnum.INDUSTRIAL_WAREHOUSE]: ['bedrooms', 'suites'],
	[PropertyTypeEnum.PENTHOUSE]: [],
	[PropertyTypeEnum.STUDIO]: [],
	[PropertyTypeEnum.TRIPLEX]: []
});

export function propertyCountableExistsOnPropertyType(
	propertyType: PropertyTypeEnum,
	propertyCountableName: keyof Property
): boolean {
	return !hiddenFields[propertyType]?.includes(propertyCountableName);
}

export const propertyTypeByCondoType: Record<PropertyTypeEnum, CondoTypeEnum> =
	{
		[PropertyTypeEnum.APARTMENT]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.COMMERCIAL]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.COMMERCIAL_BUILDING]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.COMMERCIAL_POINT]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.COMMERCIAL_SET]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.DUPLEX]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.FLAT]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.GARDEN]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.LOFT]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.PENTHOUSE]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.STUDIO]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.TRIPLEX]: CondoTypeEnum.VERTICAL,
		[PropertyTypeEnum.HOUSE]: CondoTypeEnum.HORIZONTAL,
		[PropertyTypeEnum.VILLAGE_HOUSE]: CondoTypeEnum.HORIZONTAL,
		[PropertyTypeEnum.CONDO_HOUSE]: CondoTypeEnum.HORIZONTAL,
		[PropertyTypeEnum.CONDO_LAND]: CondoTypeEnum.HORIZONTAL,
		[PropertyTypeEnum.INDUSTRIAL_WAREHOUSE]: CondoTypeEnum.HORIZONTAL,
		[PropertyTypeEnum.LAND]: CondoTypeEnum.HORIZONTAL
	};
